/**
 * KANU PET — kp-main-product.js
 * Interactividad de la sección main-product:
 *  - Galería: cambio de imagen por miniatura + carrusel
 *  - Variantes: selección de pills → precio, disponibilidad, imagen, URL, Addi
 *  - Cantidad: + / –
 *  - Carrito: agregar por AJAX con loader
 *  - Addi: botón "Más info"
 *
 * v1.0 | Junio 2026
 * v1.1 | Septiembre 2026
 *  - El botón de carrito conserva su ícono y los textos del editor
 *    (antes se reemplazaban por "Agregar al carrito"/"Agotado" fijos
 *    y se perdía el ícono al cambiar de variante o al agregar).
 *  - Precios con el formato de moneda de la tienda (igual que Liquid);
 *    antes usaba otro formato y el precio "saltaba" al cambiar variante.
 *  - El precio respeta los colores del bloque Precio.
 *  - La imagen de la variante reemplaza correctamente la imagen principal
 *    (se quita srcset) y marca su miniatura como activa.
 *  - Cantidad: no falla si faltan los botones + / –.
 *  - Tras agregar al carrito, el botón vuelve a su estado real
 *    (si la variante está agotada, sigue deshabilitado).
 *  - Se reinicia en el editor de temas al editar la sección.
 *  - Se carga con "defer" desde main-product (no bloquea la página).
 */

(function () {
  'use strict';

  /* ── Helpers ───────────────────────────────────────── */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  /* ── Formato de moneda (mismo que usa Shopify en Liquid) ── */
  let moneyFormat = '${{amount_no_decimals}}';

  function formatMoney(cents) {
    const value = Number(cents) || 0;
    const match = moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
    const key = match ? match[1] : 'amount_no_decimals';

    const fmt = (number, decimals, thousands, decimal) => {
      const fixed = (number / 100).toFixed(decimals);
      const [whole, frac] = fixed.split('.');
      const withSep = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? withSep + decimal + frac : withSep;
    };

    let amount;
    switch (key) {
      case 'amount':                                  amount = fmt(value, 2, ',', '.'); break;
      case 'amount_no_decimals':                      amount = fmt(value, 0, ',', '.'); break;
      case 'amount_with_comma_separator':             amount = fmt(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': amount = fmt(value, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator':        amount = fmt(value, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': amount = fmt(value, 0, ' ', ''); break;
      case 'amount_with_space_separator':             amount = fmt(value, 2, ' ', ','); break;
      default:                                        amount = fmt(value, 0, ',', '.');
    }
    return match ? moneyFormat.replace(match[0], amount) : amount;
  }

  /* ── Inicialización ────────────────────────────────── */
  function init(root) {
    const section = root && root.matches && root.matches('.kp-main-product')
      ? root
      : $('.kp-main-product', root || document);
    if (!section || section.dataset.kpInit === 'true') return;
    section.dataset.kpInit = 'true';

    const priceRow = $('#kp-price-row', section);
    if (priceRow && priceRow.dataset.moneyFormat) moneyFormat = priceRow.dataset.moneyFormat;

    initGallery(section);
    initVariants(section);
    initQuantity(section);
    initAddToCart(section);
    initAddiWidget(section);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  // Editor de temas: al guardar o cambiar un ajuste, Shopify vuelve a pintar la sección
  document.addEventListener('shopify:section:load', (e) => init(e.target));

  /* ══════════════════════════════════════════════════════
     GALERÍA
  ══════════════════════════════════════════════════════ */
  function initGallery(section) {
    const featuredImg = $('#kp-featured-img', section);
    const thumbs      = $$('.kp-gallery__thumb', section);
    const track       = $('#kp-thumbs-track', section);
    const btnPrev     = $('#kp-thumb-prev', section);
    const btnNext     = $('#kp-thumb-next', section);

    if (!featuredImg) return;

    // ── Carrusel ───────────────────────────────────────────
    // Medidas en sync con el CSS (--kp-thumb-size: 72, --kp-thumb-gap: 8)
    const THUMB_SIZE = 72;
    const THUMB_GAP  = 8;
    const THUMB_STEP = THUMB_SIZE + THUMB_GAP; // 80px por miniatura
    const WINDOW_PX  = 392;                    // 5 visibles: (72×5)+(8×4)

    let currentOffset = 0;

    const isMobile  = () => window.innerWidth <= 767;
    const maxOffset = () => Math.max(0, thumbs.length * THUMB_STEP - THUMB_GAP - WINDOW_PX);

    function applyTransform() {
      if (!track) return;
      const axis = isMobile() ? 'translateX' : 'translateY';
      track.style.transform = `${axis}(-${currentOffset}px)`;
    }

    function updateNavButtons() {
      if (btnPrev) btnPrev.disabled = currentOffset <= 0;
      if (btnNext) btnNext.disabled = currentOffset >= maxOffset();
    }

    function scrollThumbs(dir) {
      currentOffset = Math.max(0, Math.min(currentOffset + dir * THUMB_STEP, maxOffset()));
      applyTransform();
      updateNavButtons();
    }

    function scrollToThumb(index) {
      const thumbPos = index * THUMB_STEP;
      if (thumbPos < currentOffset) {
        currentOffset = thumbPos;
      } else if (thumbPos + THUMB_SIZE > currentOffset + WINDOW_PX) {
        currentOffset = thumbPos + THUMB_SIZE - WINDOW_PX;
      }
      currentOffset = Math.max(0, Math.min(currentOffset, maxOffset()));
      applyTransform();
      updateNavButtons();
    }

    if (btnPrev) btnPrev.addEventListener('click', () => scrollThumbs(-1));
    if (btnNext) btnNext.addEventListener('click', () => scrollThumbs(1));

    window.addEventListener('resize', () => {
      currentOffset = Math.min(currentOffset, maxOffset());
      applyTransform();
      updateNavButtons();
    });

    updateNavButtons();

    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => {
        const src = thumb.dataset.imageSrc;
        if (!src || thumb.classList.contains('is-active')) return;
        setActiveThumb(section, thumb);
        scrollToThumb(index);
        swapImage(featuredImg, src);
      });

      thumb.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          thumb.click();
        }
      });
    });

    // Permite que el cambio de variante mueva el carrusel
    section._kpScrollToThumb = scrollToThumb;
  }

  function setActiveThumb(section, activeThumb) {
    $$('.kp-gallery__thumb', section).forEach((t) => t.classList.remove('is-active'));
    if (activeThumb) activeThumb.classList.add('is-active');
  }

  // Cambia la imagen principal. Quita srcset/sizes: si se quedan,
  // el navegador sigue mostrando la imagen anterior.
  function swapImage(img, src, alt) {
    const apply = () => {
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.setAttribute('src', src);
      if (typeof alt === 'string') img.setAttribute('alt', alt);
      img.style.opacity = '1';
    };
    const pre = new Image();
    pre.onload = () => {
      img.style.opacity = '0';
      requestAnimationFrame(() => requestAnimationFrame(apply));
    };
    pre.onerror = apply;
    pre.src = src;
  }

  /* ══════════════════════════════════════════════════════
     VARIANTES
  ══════════════════════════════════════════════════════ */
  function initVariants(section) {
    const variantsJson = $('#kp-variants-json', section);
    if (!variantsJson) return;

    let variants;
    try {
      variants = JSON.parse(variantsJson.textContent);
    } catch (e) {
      console.error('[KP] Error leyendo las variantes', e);
      return;
    }

    $$('.kp-pill', section).forEach((pill) => {
      pill.addEventListener('click', () => {
        const optionIndex = parseInt(pill.dataset.optionIndex, 10);
        const value = pill.dataset.value;

        $$(`.kp-pill[data-option-index="${optionIndex}"]`, section)
          .forEach((p) => p.classList.remove('is-selected'));
        pill.classList.add('is-selected');

        const valDisplay = $(`#kp-option-val-${optionIndex}`, section);
        if (valDisplay) valDisplay.textContent = value;

        const selectedValues = $$('.kp-variants__group', section).map((group) => {
          const activePill = $('.kp-pill.is-selected', group);
          return activePill ? activePill.dataset.value : null;
        });

        const matched = findVariant(variants, selectedValues);
        if (matched) updateVariantUI(section, matched);
      });
    });
  }

  function findVariant(variants, selectedValues) {
    return variants.find((v) =>
      selectedValues.every((val, idx) => !val || v[`option${idx + 1}`] === val)
    ) || null;
  }

  function updateVariantUI(section, variant) {
    const variantInput = $('#kp-variant-id', section);
    if (variantInput) variantInput.value = variant.id;

    updatePrice(section, variant);
    updateAddToCartBtn(section, variant);
    updateURL(variant);
    if (variant.featured_image) updateFeaturedImage(section, variant.featured_image);
    updateAddi(section, variant);
  }

  function updatePrice(section, variant) {
    const priceRow = $('#kp-price-row', section);
    if (!priceRow) return;

    const d = priceRow.dataset;
    const useYagi      = d.useYagi === 'true';
    const yagiPct      = parseFloat(d.yagiPct || '0');
    const shopPct      = parseFloat(d.shopPct || '0');
    const fontSize     = d.fontSize || '30';
    const showTaxes    = d.showTaxes === 'true';
    const priceColor   = d.priceColor || '';
    const compareColor = d.compareColor || '';

    const price = variant.price;
    const compareAt = variant.compare_at_price && variant.compare_at_price > price
      ? variant.compare_at_price
      : 0;

    // Lógica de Yagi: descuento del producto; el de la tienda lo sobreescribe
    let discountedPrice = price;
    let hasYagiDiscount = false;
    [yagiPct, shopPct].forEach((pct) => {
      if (pct > 0.01) {
        discountedPrice = Math.ceil((price / 100) * (1 - pct) * 100);
        hasYagiDiscount = true;
      }
    });

    const hasDiscount = useYagi && hasYagiDiscount && discountedPrice < price;
    const strikePrice = compareAt || price;

    const discountPct = hasDiscount
      ? Math.round((price - discountedPrice) * 100 / price)
      : (compareAt ? Math.round((compareAt - price) * 100 / compareAt) : 0);

    const taxesHtml   = showTaxes ? '<span class="kp-price__taxes">IVA incluido</span>' : '';
    const compareCss  = compareColor ? ` style="color:${compareColor};"` : '';
    const currentCss  = (color) => `font-size:${fontSize}px;font-weight:800;${color ? `color:${color};` : ''}`;
    const badgeHtml   = discountPct > 0
      ? `<span class="kp-discount-badge" id="kp-discount-badge">-${discountPct}%</span>`
      : '';

    if (hasDiscount) {
      priceRow.innerHTML = `
        <div class="kp-price kp-price--on-sale" id="kp-price-display">
          <span class="kp-price__compare" id="kp-price-compare"${compareCss}><s>${formatMoney(strikePrice)}</s></span>
          <span class="kp-price__current" id="kp-price-current" style="${currentCss('var(--kp-orange)')}">${formatMoney(discountedPrice)}</span>
          ${taxesHtml}
        </div>
        ${badgeHtml}`;
    } else {
      const showCompare = !useYagi && compareAt;
      priceRow.innerHTML = `
        <div class="kp-price" id="kp-price-display">
          <span class="kp-price__current" id="kp-price-current" style="${currentCss(priceColor)}">${formatMoney(price)}</span>
          ${showCompare ? `<span class="kp-price__compare" id="kp-price-compare"${compareCss}><s>${formatMoney(compareAt)}</s></span>` : ''}
          ${badgeHtml}
          ${taxesHtml}
        </div>`;
    }
  }

  // Cambia solo el texto del botón y conserva el ícono
  function setBtnLabel(btn, label, showIcon) {
    const textEl = $('.kp-btn__text', btn);
    if (!textEl) return;
    if (!btn._kpIcon) {
      const icon = $('.kp-btn__icon', textEl);
      if (icon) btn._kpIcon = icon.cloneNode(true);
    }
    textEl.textContent = '';
    if (showIcon && btn._kpIcon) {
      textEl.appendChild(btn._kpIcon.cloneNode(true));
      textEl.appendChild(document.createTextNode(' '));
    }
    textEl.appendChild(document.createTextNode(label));
  }

  function updateAddToCartBtn(section, variant) {
    const btn = $('#kp-add-to-cart', section);
    if (!btn) return;
    const labelAdd     = btn.dataset.labelAdd || 'Agregar al carrito';
    const labelSoldOut = btn.dataset.labelSoldout || 'Agotado';

    btn.dataset.available = variant.available ? 'true' : 'false';
    btn.disabled = !variant.available;
    setBtnLabel(btn, variant.available ? labelAdd : labelSoldOut, variant.available);
  }

  function updateURL(variant) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  function updateFeaturedImage(section, image) {
    const featuredImg = $('#kp-featured-img', section);
    const src = image && image.src;
    if (!featuredImg || !src) return;

    const thumb = image.id ? $(`.kp-gallery__thumb[data-image-id="${image.id}"]`, section) : null;
    if (thumb) {
      setActiveThumb(section, thumb);
      const index = $$('.kp-gallery__thumb', section).indexOf(thumb);
      if (section._kpScrollToThumb && index >= 0) section._kpScrollToThumb(index);
    }
    // Usa la versión de 900px (la misma que las miniaturas) si existe
    swapImage(featuredImg, (thumb && thumb.dataset.imageSrc) || src, image.alt || featuredImg.alt);
  }

  function updateAddi(section, variant) {
    const addiEl = $('.kp-addi__main', section);
    if (!addiEl) return;
    const cuota = Math.round(variant.price / 4);
    addiEl.innerHTML = `Paga en 4 cuotas de <strong>${formatMoney(cuota)}</strong>`;
  }

  /* ══════════════════════════════════════════════════════
     CANTIDAD
  ══════════════════════════════════════════════════════ */
  function initQuantity(section) {
    const qtyWrap = $('#kp-qty-wrap', section);
    if (!qtyWrap) return;

    const input    = $('.kp-qty__input', qtyWrap);
    const btnMinus = $('.kp-qty__btn--minus', qtyWrap);
    const btnPlus  = $('.kp-qty__btn--plus', qtyWrap);
    if (!input) return;

    input.setAttribute('step', '1');

    const setQty = (val) => {
      const n = Math.max(1, Math.min(99, parseInt(val, 10) || 1));
      input.value = n;
      if (btnMinus) btnMinus.disabled = n <= 1;
    };

    if (btnMinus) btnMinus.addEventListener('click', (e) => { e.preventDefault(); setQty(Number(input.value) - 1); });
    if (btnPlus)  btnPlus.addEventListener('click',  (e) => { e.preventDefault(); setQty(Number(input.value) + 1); });
    input.addEventListener('change', () => setQty(input.value));

    setQty(input.value);
  }

  /* ══════════════════════════════════════════════════════
     AGREGAR AL CARRITO — AJAX
  ══════════════════════════════════════════════════════ */
  function initAddToCart(section) {
    const form = $('#kp-product-form', section);
    if (!form) return;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const btn = $('#kp-add-to-cart', section);
      const variantId = $('#kp-variant-id', section)?.value;
      const qty = parseInt($('.kp-qty__input', section)?.value || '1', 10);
      if (!variantId || !btn || btn.disabled) return;

      setLoading(btn, true);

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: qty }),
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.description || `Error ${res.status}`);

        updateCartCount();
        document.dispatchEvent(new CustomEvent('kp:cart:itemAdded', { detail: { item: data } }));
        showFeedback(btn, '✓ Agregado', '#2EB84B', 1800);
      } catch (err) {
        console.error('[KP] Error al agregar al carrito', err);
        showFeedback(btn, 'Error, intenta de nuevo', '#D32F2F', 2500);
      } finally {
        setLoading(btn, false);
      }
    });
  }

  function setLoading(btn, isLoading) {
    btn.classList.toggle('kp-btn--loading', isLoading);
    // Al terminar, vuelve al estado real de la variante (agotada = deshabilitado)
    btn.disabled = isLoading || btn.dataset.available === 'false';
  }

  function showFeedback(btn, text, color, ms) {
    const labelAdd = btn.dataset.labelAdd || 'Agregar al carrito';
    setBtnLabel(btn, text, false);
    btn.style.background = color;
    clearTimeout(btn._kpFeedbackTimer);
    btn._kpFeedbackTimer = setTimeout(() => {
      const available = btn.dataset.available !== 'false';
      setBtnLabel(btn, available ? labelAdd : (btn.dataset.labelSoldout || 'Agotado'), available);
      btn.style.background = '';
    }, ms);
  }

  async function updateCartCount() {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.setAttribute('aria-label', `${cart.item_count} artículos en el carrito`);
      });
    } catch (_) { /* sin contador en el header */ }
  }

  /* ══════════════════════════════════════════════════════
     ADDI
     El script de Addi busca el elemento indicado en
     data-element-reference. Lo apuntamos a #kp-add-to-cart.
     "Más info" intenta abrir el widget; si no responde,
     usa la URL configurada en el bloque.
  ══════════════════════════════════════════════════════ */
  function initAddiWidget(section) {
    const addiScript = document.querySelector('[data-name="shopifyAddiWidget"]');
    if (addiScript) addiScript.setAttribute('data-element-reference', '#kp-add-to-cart');

    const infoBtn = $('#kp-addi-info-btn', section);
    if (!infoBtn) return;

    infoBtn.addEventListener('click', () => {
      const selectors = ['.addi-widget-button', '[data-addi-widget]', '.addi-button', '#addi-widget-button', '.addi_widget_button'];
      let triggered = false;

      for (const sel of selectors) {
        const el = document.querySelector(sel);
        if (el) { el.click(); triggered = true; break; }
      }

      if (!triggered) {
        document.dispatchEvent(new CustomEvent('addi:open', { bubbles: true }));
        const refEl = document.querySelector('#kp-add-to-cart');
        if (refEl) refEl.dispatchEvent(new CustomEvent('addi:open', { bubbles: true }));

        const api = window.AddiWidget || window.addiWidget;
        if (api && typeof api.open === 'function') {
          api.open();
          triggered = true;
        }
      }

      if (!triggered) {
        const fallbackUrl = infoBtn.dataset.fallbackUrl;
        if (fallbackUrl) {
          if (infoBtn.dataset.fallbackNewTab === 'true') {
            window.open(fallbackUrl, '_blank', 'noopener');
          } else {
            window.location.href = fallbackUrl;
          }
        }
      }
    });
  }
})();
